/* ==========================================================================
   Yavru Patiler — global theme behavior
   Lightweight, dependency-free. Progressive enhancement only.
   Safe to re-run (Shopify theme editor) — every initializer is idempotent.
   ========================================================================== */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Reveal on scroll ---- */
  function initReveal(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = el.getAttribute('data-reveal-delay');
          if (delay) el.style.setProperty('--reveal-delay', delay);
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(function (el) { if (!el.classList.contains('is-visible')) io.observe(el); });
  }

  /* ---- Header: shrink on scroll + mobile drawer (with focus trap) ---- */
  var headerScrollBound = false;
  function initHeader() {
    var ann = document.querySelector('.announce');
    document.documentElement.style.setProperty('--announce-h', (ann ? ann.offsetHeight : 0) + 'px');
    var header = document.querySelector('[data-header]');
    if (!header) return;

    if (!headerScrollBound) {
      headerScrollBound = true;
      var ticking = false;
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var h = document.querySelector('[data-header]');
          if (h) h.classList.toggle('is-scrolled', window.pageYOffset > 24);
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    if (header.dataset.inited) return;
    header.dataset.inited = '1';

    var toggle = header.querySelector('[data-nav-toggle]');
    var drawer = document.querySelector('[data-nav-drawer]');
    if (!toggle || !drawer) return;
    var panel = drawer.querySelector('.nav-drawer__panel') || drawer;

    function focusables() {
      return Array.prototype.slice
        .call(panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return el.offsetParent !== null; });
    }

    function setOpen(open, returnFocus) {
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.classList.toggle('nav-open', open);
      if (open) {
        var f = focusables();
        if (f.length) f[0].focus();
      } else if (returnFocus !== false) {
        toggle.focus();
      }
    }

    toggle.addEventListener('click', function () {
      setOpen(!drawer.classList.contains('is-open'));
    });
    drawer.addEventListener('click', function (e) {
      // Accordion parent (e.g. Güncel Yavrularımız) expands its sub-links in place
      var acc = e.target.closest('[data-acc-toggle]');
      if (acc) {
        var expanded = acc.getAttribute('aria-expanded') === 'true';
        acc.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var panel = acc.parentNode.querySelector('[data-acc-panel]');
        if (panel) panel.hidden = expanded;
        return;
      }
      if (e.target.closest('[data-nav-close]')) { setOpen(false); return; }
      // Any real link (or a click on its child) navigates — close without stealing focus
      if (e.target.closest('a[href]')) setOpen(false, false);
    });
    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key === 'Tab') {
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---- Language: persist preference ---- */
  function initLang() {
    document.querySelectorAll('[data-lang-link]').forEach(function (link) {
      if (link.dataset.langBound) return;
      link.dataset.langBound = '1';
      link.addEventListener('click', function () {
        try { localStorage.setItem('yp-lang', link.getAttribute('data-lang-link')); } catch (e) {}
      });
    });
  }

  /* ---- Video pause/play toggles (WCAG 2.2.2) ---- */
  function initVideoToggles(root) {
    (root || document).querySelectorAll('[data-video-toggle]').forEach(function (btn) {
      if (btn.dataset.vtInit) return;
      btn.dataset.vtInit = '1';
      var video = btn.parentElement.querySelector('video');
      if (!video) return;
      function setState(paused) {
                btn.setAttribute('aria-label', paused ? btn.getAttribute('data-label-play') : btn.getAttribute('data-label-pause'));
      }
      var motionOff = document.documentElement.getAttribute('data-yp-motion') === 'off';
      if (prefersReduced || motionOff) {
        video.removeAttribute('autoplay');
        video.pause();
        setState(true);
      } else {
        setState(false);
      }
      btn.addEventListener('click', function () {
        if (video.paused) { video.play(); setState(false); }
        else { video.pause(); setState(true); }
      });
    });
  }

  /* ---- Marquee: duplicate for a seamless loop, hide the copy from AT ---- */
  function initMarquee(root) {
    var motionOff = document.documentElement.getAttribute('data-yp-motion') === 'off';
    (root || document).querySelectorAll('[data-marquee]').forEach(function (track) {
      if (prefersReduced || motionOff || track.dataset.marqueeInit) return;
      track.dataset.marqueeInit = '1';
      var original = track.children.length;
      track.innerHTML = track.innerHTML + track.innerHTML;
      var kids = track.children;
      for (var i = original; i < kids.length; i++) kids[i].setAttribute('aria-hidden', 'true');
    });
  }

  /* ---- Accessible accordion (FAQ) ---- */
  function initAccordion(root) {
    (root || document).querySelectorAll('[data-accordion] [data-accordion-trigger]').forEach(function (trigger) {
      if (trigger.dataset.accInit) return;
      trigger.dataset.accInit = '1';
      trigger.addEventListener('click', function () {
        var item = trigger.closest('[data-accordion-item]');
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        var group = trigger.closest('[data-accordion]');
        if (group && group.hasAttribute('data-accordion-single')) {
          group.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]').forEach(function (other) {
            if (other !== trigger) {
              other.setAttribute('aria-expanded', 'false');
              var p = other.closest('[data-accordion-item]');
              if (p) p.classList.remove('is-open');
            }
          });
        }
        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        item.classList.toggle('is-open', !expanded);
      });
    });
  }

  /* ---- Horizontal scroll strips (.hscroll): drag, buttons, progress ---- */
  function initCarousels(root) {
    (root || document).querySelectorAll('[data-hscroll]').forEach(function (box) {
      if (box.dataset.hsInit) return;
      box.dataset.hsInit = '1';
      var track = box.querySelector('.hscroll__track');
      if (!track) return;
      var prev = box.querySelector('[data-hscroll-prev]');
      var next = box.querySelector('[data-hscroll-next]');
      var bar = box.querySelector('.hscroll__progress-bar');

      function step() {
        var cell = track.querySelector('.hscroll__cell');
        return cell ? cell.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;
        if (bar) {
          var visible = max <= 0 ? 1 : Math.max(track.clientWidth / track.scrollWidth, 0.12);
          var p = max <= 0 ? 0 : track.scrollLeft / max;
          bar.style.setProperty('--hp', (visible + p * (1 - visible)).toFixed(4));
        }
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step() * 2, behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step() * 2, behavior: 'smooth' }); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });

      /* Desktop drag-to-scroll */
      if (window.matchMedia('(pointer: fine)').matches) {
        var startX = 0, startLeft = 0, dragging = false, moved = false;
        track.addEventListener('pointerdown', function (e) {
          dragging = true; moved = false;
          startX = e.clientX; startLeft = track.scrollLeft;
        });
        window.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          var dx = e.clientX - startX;
          if (Math.abs(dx) > 6 && !moved) { moved = true; track.classList.add('is-dragging'); }
          if (moved) track.scrollLeft = startLeft - dx;
        });
        window.addEventListener('pointerup', function () {
          if (!dragging) return;
          dragging = false;
          if (moved) setTimeout(function () { track.classList.remove('is-dragging'); }, 30);
        });
        track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      }
      update();
    });
  }

  /* ---- Section dots: right-edge page nav, built from on-page section ids ---- */
  function initSectionDots() {
    var nav = document.querySelector('[data-section-dots]');
    if (!nav || nav.dataset.sdInit) return;
    nav.dataset.sdInit = '1';
    var sections = Array.prototype.slice
      .call(document.querySelectorAll('main [id]'))
      .filter(function (el) { return /^(top|yavrular|cinsler|eslestirici|yolculuk|guven|hikaye|isim-studyosu|mutlu-aileler|ekip|sss|son-cta|rakamlar|sec-yolu)$/.test(el.id); });
    if (sections.length < 4) return;
    var dots = sections.map(function (sec) {
      var label = (sec.querySelector('h1, h2') || {}).textContent || sec.id;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'section-dots__dot';
      b.innerHTML = '<span class="section-dots__label">' + label.trim().slice(0, 32) + '</span>';
      b.setAttribute('aria-label', label.trim().slice(0, 60));
      b.addEventListener('click', function () { sec.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
      nav.appendChild(b);
      return b;
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var idx = sections.indexOf(e.target);
          dots.forEach(function (d, i) {
            d.classList.toggle('is-active', i === idx);
            if (i === idx) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-40% 0px -55% 0px' });
      sections.forEach(function (s) { io.observe(s); });
    }
    nav.classList.add('is-ready');
  }

  /* ---- Contact-click tracking (wa.me / tel:) — first-party, no cookies ---- */
  var trackBound = false;
  function initTracking() {
    if (trackBound) return;
    if (document.documentElement.getAttribute('data-yp-track') === 'false') return;
    trackBound = true;
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="https://wa.me"], a[href^="tel:"]');
      if (!a) return;
      var channel = a.href.indexOf('wa.me') > -1 ? 'whatsapp' : 'phone';
      var section = a.closest('section[id], div[id], [data-header], .footer-v2, .sticky-rail, .pc');
      var payload = {
        event: 'yp_contact_click',
        channel: channel,
        placement: (section && (section.id || section.className.split(' ')[0])) || 'page',
        page: location.pathname
      };
      try { if (window.dataLayer) window.dataLayer.push(payload); } catch (err) {}
      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
          Shopify.analytics.publish('yp_contact_click', payload);
        }
      } catch (err) {}
    }, { passive: true });
  }

  /* ---- Hover-to-play card videos ----
     Any element with [data-hover-video="<mp4 url>"]: a muted looping <video>
     is lazily created inside its media box ('.card-media', '.breed-card__media'
     or [data-hover-video-slot]) and fades in over the photo.
     Fine pointers: play on hover, pause on leave.
     Touch (no hover): play automatically while the card is mostly in view
     ("its turn"), pause the moment it scrolls out.
     prefers-reduced-motion disables both. */
  var hvActive = null; // touch mode: only ONE card video plays at a time
  function initHoverVideo(root) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // respect the theme's own motion setting, like every motion.js feature
    var ypMotion = document.documentElement.getAttribute('data-yp-motion');
    if (ypMotion === 'off' || ypMotion === 'minimal') return;
    var finePointer = window.matchMedia('(pointer: fine)').matches;

    (root || document).querySelectorAll('[data-hover-video]').forEach(function (host) {
      if (host.dataset.hvInit) return;
      host.dataset.hvInit = '1';
      var src = host.getAttribute('data-hover-video');
      if (!src) return;
      var slot = host.querySelector('[data-hover-video-slot], .card-media, .breed-card__media') || host;
      var video = null;

      function ensureVideo() {
        if (video) return video;
        video = document.createElement('video');
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
        video.preload = 'none'; // don't buffer until the card actually plays
        video.className = 'hover-video';
        video.src = src;
        video.addEventListener('canplay', function () { video.classList.add('is-ready'); });
        slot.appendChild(video);
        return video;
      }
      function start() {
        ensureVideo().classList.add('is-on');
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
      function stop() {
        if (!video) return;
        video.classList.remove('is-on');
        video.pause();
      }

      if (finePointer) {
        host.addEventListener('pointerenter', start);
        host.addEventListener('pointerleave', stop);
      } else if ('IntersectionObserver' in window) {
        // Touch: a card plays only while it holds the stage — and only one at a
        // time (the latest to cross 60%), so strips never play 2-3 loops at once.
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.intersectionRatio >= 0.6) {
              if (hvActive && hvActive !== stop) hvActive();
              hvActive = stop;
              start();
            } else if (hvActive === stop) {
              stop();
              hvActive = null;
            } else {
              stop();
            }
          });
        }, { threshold: [0, 0.6] });
        io.observe(host);
      }
    });
  }

  function init(root) {
    initReveal(root);
    initHeader();
    initLang();
    initVideoToggles(root);
    initMarquee(root);
    initAccordion(root);
    initCarousels(root);
    initSectionDots();
    initTracking();
    initHoverVideo(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* ---- Shopify theme editor: re-wire reloaded sections ---- */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
